import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';

const ALT_TYPES = ['SKU', 'ASIN', 'GTIN'];

// Alternate identifiers (old SKUs / ASINs / GTINs) mapped to the main SKU.
// Used by all marketplace file uploads to resolve rows to the main product.
// Rendered in edit mode only (needs an existing product SKU).
const AlternateIdsField = ({ productType, sku }) => {
    const [items, setItems] = useState([]);
    const [altType, setAltType] = useState('SKU');
    const [altValue, setAltValue] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(() => {
        if (!sku) return;
        api.get(`/api/product-alternate-ids`, { params: { sku } })
            .then(r => setItems(Array.isArray(r.data) ? r.data : []))
            .catch(() => setItems([]));
    }, [sku]);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async () => {
        const value = altValue.trim();
        if (!value) return;
        setError('');
        try {
            await api.post(`/api/product-alternate-ids`, {
                product_type: productType,
                product_sku: sku,
                alt_type: altType,
                alt_value: value,
            });
            setAltValue('');
            load();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add');
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/api/product-alternate-ids/${id}`);
            setItems(prev => prev.filter(i => i.id !== id));
        } catch {}
    };

    if (!sku) return null;

    return (
        <div className="kit-field" style={{ gridColumn: '1 / -1' }}>
            <span className="kit-field__label" title="Old / alternative identifiers used on marketplaces — file uploads map them to this product">
                Alternate IDs (old SKU / ASIN / GTIN)
            </span>

            {items.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 0 6px' }}>
                    {items.map(item => (
                        <span key={item.id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: '#f1f7f2', border: '1px solid #cfe3d1', borderRadius: 12,
                            padding: '3px 8px', fontSize: 12,
                        }}>
                            <span style={{ fontWeight: 700, color: '#437045', fontSize: 10 }}>{item.alt_type}</span>
                            {item.alt_value}
                            <button type="button" onClick={() => handleDelete(item.id)} title="Remove"
                                style={{ border: 'none', background: 'none', color: '#9ba3b0', cursor: 'pointer', padding: 0, fontSize: 11, lineHeight: 1 }}>
                                <i className="fas fa-times"></i>
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <select value={altType} onChange={e => setAltType(e.target.value)}
                    style={{ padding: '6px 6px', border: '1px solid #dde1e7', borderRadius: 6, fontSize: 13, background: '#fff' }}>
                    {ALT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input
                    value={altValue}
                    onChange={e => setAltValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
                    placeholder="Old identifier value…"
                    style={{ flex: 1, padding: '6px 8px', border: '1px solid #dde1e7', borderRadius: 6, fontSize: 13, minWidth: 0 }}
                />
                <button type="button" onClick={handleAdd} disabled={!altValue.trim()}
                    style={{
                        padding: '6px 12px', background: altValue.trim() ? '#28a745' : '#94A3B8',
                        color: '#fff', border: 'none', borderRadius: 6, fontSize: 13,
                        cursor: altValue.trim() ? 'pointer' : 'not-allowed', fontWeight: 600,
                    }}>
                    <i className="fas fa-plus" style={{ marginRight: 4 }}></i>Add
                </button>
            </div>
            {error && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 4 }}>⚠ {error}</div>}
        </div>
    );
};

export default AlternateIdsField;
